/* eslint-disable no-underscore-dangle */
import { debounce } from '@quillbot/design-system';
import Track from 'core/logger/track';
import { ProjectStateActionName } from 'src/researcher/types/project';
import { CitationData } from 'src/citation-generator/types';
import { BaseEditorAutoCompleteSourceData } from '../models/IBaseEditorTypes';
import {
  AutoSuggestModelName,
  Suggestions,
  SYSTEM_SUGGESTION_SHOWN,
  USER_SELECTED_SUGGESTION,
} from '../types/autosuggest';
import {
  CreateProjectTriggerBy,
  PasteTriggeredByEnum,
  ScraperType,
  SearchCategories,
} from '../types/common';
import { ManifoldTabEnum } from '../types/toolpane';
import { BookmarkActionNameEnum } from '../types/bookmark';
import { ResearchActionNameEnum } from '../types/research';
import { ActionTriggerBy, ContextMenuContainer } from '../types/contextmenu';
import { TOGGLE_PAINT_GRAMMAR_RESPONSE } from '../types/grammar';

export enum LaunchSearchTriggeredBy {
  ResearchTab = 'RESEARCH_TAB',
  ContextMenu = 'CONTEXT_MENU',
}

interface TabSwitchOptions {
  from?: any;
  parent?: any;
  isSwitchedByUser?: boolean;
}

class ResearcherTrack {
  private _track: Track;

  /**
   * Here goes events which doesn't modify state of any store.
   * These events may be just handled by a internal state of
   * the component or some other action is being reused thus
   * these events are not related to any store. And doesn't need a
   * mention in action name.
   */

  private _events = {
    SelectedExistingProject: 'rshr/user/selected_existing_project',
    ClickedCopyAllButton: 'rshr/user/clicked_copy_all',
    ClickedDeleteProject: 'rshr/user/clicked_delete_project',
    EditorToolbarActionFired: 'rshr/system/toolbar_action_fired',
    ClickedEditorDeleteAllText: 'rshr/user/clicked_delete_text',
    UserPastedText: 'rshr/user/pasted_text',
    gcResponsePainted: 'rshr/system/grammar_checker_response_painted',
    gcCorrectionCardOpen: 'rshr/user/opened_grammar_correction_card',
    gcIgnoreCorrection: 'rshr/user/clicked_ignore_error',
    gcClickedCorrectError: 'rshr/user/clicked_correct_error',
    userClickedParaphraseButton: 'rshr/user/clicked_popover_paraphrase_button',
    userClickedSummarizerButton: 'rshr/user/clicked_popover_summarize_button',
    userClickedAddCitationButton: 'rshr/user/clicked_popover_add_citation_button',
    userTriggeredOnBoarding: 'rshr/user/triggered_onboarding',
    voiceTranscript: 'rshr/user/voice_transcript',
    projectExport: 'rshr/user/project_export',
    UserClickedCorrectAllGcErrorsButton: 'rshr/user/clicked_correct_all_button',
    UserResizePaneView: 'rshr/user/resize_pane',
    UserInsertToEditor: 'rshr/user/insert_to_editor',
    UserSearchProjectTriggered: 'rshr/user/search_project',
    UserOpenProjectModal: 'rshr/user/open_project_modal',
    UserSearchResource: 'rshr/user/searched_query',
    UserOpenedCardHighlights: 'rshr/user/opened_higlights',
    UserClickedViewAllHighlights: 'rshr/user/clicked_view_all_highlights',
    UserInteractedWithHighlights: 'rshr/user/interacted_with_highlights',
    UserClickedOpenResult: 'rshr/user/clicked_open_result',
    UserOpenedStatsAccordion: 'rshr/user/opened_stat',
    UserSelectedTopics: 'rshr/user/selected_topic_chip',
    UserClickedResultCardSummarize: 'rshr/user/clicked_result_card_summarize_button',
    SkipOnboarding: 'rshr/user/onboarding/skip',
    CloseOnboarding: 'rshr/user/onboarding/complete',
    UserReachedStorageQuota: 'rshr/user/reached_storage_quota',
    SystemTriggeredPremiumUpsellModal: 'rshr/user/triggered_wa_premium_upsell',
    UserClickedCitationMarker: 'rshr/user/clicked_citation_marker',
    TriggeredAutocompletion: 'rshr/user/triggered_autocompleter',
    ClickedParaphraseSummaryButton: 'rshr/user/clicked_paraphrase_summary_button',
    ClickedRephraseButton: 'rshr/user/clicked_rephrase_button',
    ReachedLastSlide: 'rshr/user/onboarding/last_slide',
    UserPerformedAction: 'rshr/user/onboarding/user_performed_action',
    UserUsedNavigationButton: 'rshr/user/onboarding/user_used_navigation_button',
    SearchWithinCitation: 'rshr/user/searched_within_citations',
    UserInteractedWithScratchpad: 'rshr/user/notebook/added_scratchpad_content',
    UserSwitchTab: 'rshr/user/switched_tab',
    SystemSwitchTab: 'rshr/system/switched_tab',
    ClickedResultCardURL: 'rshr/user/clicked_result_card_url',
    UserDeleteCitation: 'rshr/user/clicked_delete_citation',
    UserClickedEditCitation: 'rshr/user/clicked_edit_citation',
    UserClickedFindOnPage: 'rshr/user/clicked_find_citation_on_page',
    UserClickedCitationSource: 'rshr/user/clicked_citation_source',
    UserClickedCardAddCitationButton: 'rshr/user/clicked_add_citation_button',
    UserClosedAddCitationModal: 'rshr/user/clicked_close_citation_modal',
    UserClickedAddCitationMarkerButton: 'rshr/user/clicked_add_citation_marker_button',
    UserChangeCitationStyle: 'rshr/user/changed_citation_stlye',
    UserSubmittedCitationFromModal: 'rshr/user/clicked_submit_citation_modal',
    WriteAssistTooltipShown: 'rshr/user/write_assist_tooltip_shown',
    UserClickedHelpButton: 'rshr/user/clicked_help_button',
    UserClickedRenameProject: 'rshr/user/clicked_rename_project',
    UserSwitchedView: 'rshr/user/switched_view',
    UserSortedProject: 'rshr/user/sorted_project',
    UserOpenedTrashView: 'rshr/user/opened_trash_view',
    UserOpenedProjectNewTab: 'rshr/user/opened_project_new_tab',
    UserRestoredProject: 'rshr/user/restored_project',
    UserExitedProject: 'rshr/user/exited_project',
    UserOpenedFeedback: 'rshr/user/opened_feedback',
    UserInteractedWithOnboarding: 'rshr/user/onboarding/interacted_with_onboarding',
    UserToggledBookmarkDrawer: 'rshr/user/toggled_bookmark_drawer',
    UserOpenedLinkNewTab: 'rshr/user/opened_link_new_tab',
    UserOpenedSection: 'rshr/user/opened_section',
    UserMovedParagraph: 'rshr/user/moved_paragraph',
    UserChangedSummaryMode: 'rshr/user/changed_summary_mode',
    UserChangedSummaryLength: 'rshr/user/changed_summary_length',
    UserOpenedPopper: 'rshr/user/opened_popper',
    UserClickedResultCard: 'rshr/user/clicked_result_card',
  };

  private _countInteractedWithHighlights = 0;

  private _countInteractedWithOnboarding = 0;

  private _countAddedScratchpadContent = 0;

  // storageOccupied between 80 to 90
  private _countInfoStorage = 0;

  // storageOccupied between 90 to 99
  private _countWarningStorage = 0;

  // storageOccupied between 99 to 100
  private _countCriticalStorage = 0;

  constructor() {
    this._track = Track.getInstance();
  }

  /**
   * Any Triggering of tabs inside toolpane
   */

  private _switchTab = (
    active: string,
    { from, parent, isSwitchedByUser = true }: TabSwitchOptions = {}
  ) => {
    let actionName = this._events.UserSwitchTab;
    if (!isSwitchedByUser) actionName = this._events.SystemSwitchTab;
    this._track.ampEvent(actionName, { active, from, parent });
    this._track.storeActionEvent(actionName, { active, from, parent });
  };

  /** WRITER AREA */

  /**
   * Called when existing project is restored from  list of existing projects,
   * Note: This is not a store action as it is similar to restore project
   * so putting constant here is okay.
   */

  selectedExistingProject = () => {
    this._track.ampEvent(this._events.SelectedExistingProject);
    this._track.storeActionEvent(this._events.SelectedExistingProject);
  };

  /**
   * Called whenever a new project is created,
   * project is created by:
   * - clicking on create-new button modal
   * - uploading project from modal
   * - closing doc modal
   * - when no project exist thus we create new project in bg
   */

  createdNewProject = (triggeredBy: any) => {
    this._track.ampEvent(ProjectStateActionName.CreatedProject, { triggeredBy });
    this._track.storeActionEvent(ProjectStateActionName.CreatedProject, { triggeredBy });
  };

  /**
   * Edit project name in writer
   */

  clickedRenameProject = (triggeredBy: 'Direct' | 'File Menu') => {
    this._track.ampEvent(this._events.UserClickedRenameProject, { triggeredBy });
    this._track.storeActionEvent(this._events.UserClickedRenameProject, { triggeredBy });
  };

  /**
   * Delete project from project modal
   */

  deletedProject = (
    type: 'Temporary' | 'Permanent',
    triggeredBy: 'Trash View' | 'Landing Page' | 'File Menu'
  ) => {
    this._track.ampEvent(this._events.ClickedDeleteProject, { type, triggeredBy });
    this._track.storeActionEvent(this._events.ClickedDeleteProject, { type, triggeredBy });
  };

  /**
   * Copied writer area all text
   */

  clickedCopyButton = (text: string, numberOfWords: number) => {
    this._track.ampEvent(this._events.ClickedCopyAllButton, { numberOfWords });
    this._track.storeActionEvent(this._events.ClickedCopyAllButton, { numberOfWords });
  };

  /**
   * Tiny Mce actions
   */

  firedControlAction = (command: string) => {
    this._track.ampEvent(this._events.EditorToolbarActionFired, { command });
    this._track.storeActionEvent(this._events.EditorToolbarActionFired, { command });
  };

  /**
   * clear write pane input by delete text button
   */

  clickedDeleteText = (text: string) => {
    this._track.ampEvent(this._events.ClickedEditorDeleteAllText);
    this._track.storeActionEvent(this._events.ClickedEditorDeleteAllText, { text });
  };

  /**
   * User pasted text in writer
   */

  userPastedText = (triggeredBy?: PasteTriggeredByEnum, withoutFormatting?: boolean) => {
    this._track.ampEvent(this._events.UserPastedText, {
      triggeredBy: triggeredBy || PasteTriggeredByEnum.HOTKEY,
      withoutFormatting,
    });
    this._track.storeActionEvent(this._events.UserPastedText);
  };

  /**
   * GRAMMAR CHECKER
   */

  grammarCheckerResponsePainted = (numberOfGrammarErrors: number) => {
    this._track.ampEvent(this._events.gcResponsePainted, { numberOfGrammarErrors });
    this._track.storeActionEvent(this._events.gcResponsePainted, {
      numberOfGrammarErrors,
    });
  };

  grammarCheckerCorrectionCardOpen = (
    errorWord: string,
    correctedWordShown: string,
    sentence: string
  ) => {
    this._track.ampEvent(this._events.gcCorrectionCardOpen);
    this._track.storeActionEvent(this._events.gcCorrectionCardOpen, {
      errorWord,
      correctedWordShown,
      sentence,
    });
  };

  ignoredGrammarError = ({
    errorWord,
    correctedWordShown,
    sentence,
  }: {
    errorWord: string;
    correctedWordShown: string;
    sentence: string;
  }) => {
    this._track.ampEvent(this._events.gcIgnoreCorrection);
    this._track.storeActionEvent(this._events.gcIgnoreCorrection, {
      errorWord,
      correctedWordShown,
      sentence,
    });
  };

  correctedGrammarError = ({
    errorWord,
    correctedWordShown,
    sentence,
  }: {
    errorWord: string;
    correctedWordShown: string;
    sentence: string;
  }) => {
    this._track.ampEvent(this._events.gcClickedCorrectError);
    this._track.storeActionEvent(this._events.gcClickedCorrectError, {
      errorWord,
      correctedWordShown,
      sentence,
    });
  };

  /**
   * CONTEXT MENU
   */
  userClickedSelectionPopoverButton = (
    buttonName: string,
    contextMenuContainer = ContextMenuContainer.COWRITER,
    triggeredBy: ActionTriggerBy
  ) => {
    let actionName = '';
    let tabName = '';
    if (buttonName === 'PARAPHRASE') {
      actionName = this._events.userClickedParaphraseButton;
      tabName = 'Paraphraser';
    } else if (buttonName === 'SUMMARIZE') {
      actionName = this._events.userClickedSummarizerButton;
      tabName = 'Summarizer';
    } else if (buttonName === 'ADD_CITATION') {
      actionName = this._events.userClickedAddCitationButton;
      tabName = 'Citations';
    } else if (buttonName === 'SELECT_AND_SEARCH') {
      tabName = 'Research';
    } else if (buttonName === 'WRITE_ASSIST') {
      actionName = this._events.TriggeredAutocompletion;
    }

    if (actionName) {
      this._track.ampEvent(actionName, { triggeredBy, from: contextMenuContainer });
      // this._track.storeActionEvent(actionName, { numberOfWords, selectionSentence });
    }
    if (tabName) {
      this._switchTab(tabName, { from: 'EDITOR_CONTEXT_MENU', isSwitchedByUser: false });
    }
  };

  /** BOTTOM CONTROL BAR */

  /**
   * Triggered training
   */
  triggeringTraning = () => {
    this._track.ampEvent(this._events.userTriggeredOnBoarding);
  };

  /**
   * voice transcript open...it can be opened as 'open' | 'delete' | 'record' | 'pause' | 'close'
   */
  voiceTranscript = (action: 'open' | 'delete' | 'record' | 'pause' | 'close', text?: string) => {
    this._track.ampEvent(this._events.voiceTranscript, { action });
  };

  /**
   * project export as docx source 'project-modal' | 'writer-pane'
   */
  projectExport = (triggeredBy: 'Landing Page' | 'File Menu' | 'Formatting Toolbar') => {
    this._track.ampEvent(this._events.projectExport, {
      triggeredBy,
    });
  };

  /**
   * Fix all grammar errors
   */

  clickedCorrectAllGrammarErrorsButton = () => {
    this._track.ampEvent(this._events.UserClickedCorrectAllGcErrorsButton);
    this._track.storeActionEvent(this._events.UserClickedCorrectAllGcErrorsButton);
  };

  /**
   * TOOLPANE COMMON
   */
  resizePaneView = debounce((paneWidth?: number) => {
    this._track.ampEvent(this._events.UserResizePaneView, {
      screenWidth: window?.screen?.availHeight || window?.screen?.width,
      screenHeight: window?.screen?.availWidth || window?.screen?.height,
      paneWidth,
    });
  }, 500);

  insertToEditor = (triggeredBy: 'voice' | 'paraphraser' | 'summarizer' | 'research') => {
    this._track.ampEvent(this._events.UserInsertToEditor, { triggeredBy });
    // this._track.storeActionEvent('rshr/user/insert_to_editor', { triggeredBy });
  };

  /** PROJECT DOC MODAL */
  searchProject = () => {
    this._track.ampEvent(this._events.UserSearchProjectTriggered);
  };

  openProjectModal = (triggeredBy: 'user' | 'system') => {
    this._track.ampEvent(this._events.UserOpenProjectModal, { triggeredBy });
  };

  /**
   * Research ToolPane
   */

  userSwitchedResourceCardTab = (active: string) => {
    this._switchTab(active, { parent: ManifoldTabEnum.RESEARCH });
  };

  userSwitchedResearchSubTab = (active: string, tabInfo?: TabSwitchOptions) => {
    this._switchTab(active, {
      from: tabInfo?.from,
      parent: tabInfo?.parent || ManifoldTabEnum.RESEARCH,
      isSwitchedByUser: tabInfo?.isSwitchedByUser,
    });
  };

  userTabSwitchInsideResultCard = (active: string, tabInfo?: TabSwitchOptions) => {
    this._switchTab(active, {
      from: tabInfo?.from,
      parent: tabInfo?.parent || ManifoldTabEnum.RESEARCH,
      isSwitchedByUser: tabInfo?.isSwitchedByUser,
    });
  };

  userSearchedQuery = ({
    searchQuery,
    searchCategory,
    searchPageNumber = 1,
    triggeredBy,
  }: {
    searchQuery: string;
    searchCategory?: SearchCategories;
    searchPageNumber?: number;
    triggeredBy?: LaunchSearchTriggeredBy;
  }) => {
    this._track.ampEvent(this._events.UserSearchResource);
    this._track.storeActionEvent(this._events.UserSearchResource, {
      searchQuery,
      searchCategory,
      searchPageNumber,
      triggeredBy,
    });
  };

  userPullSource = (url: string, scraperType: ScraperType) => {
    this._track.ampEvent(ResearchActionNameEnum.PulledSource, { url, scraperType });
    this._track.storeActionEvent(ResearchActionNameEnum.PulledSource, { url, scraperType });
  };

  userPullSourceError = (url: string, scraperType: ScraperType) => {
    this._track.ampEvent(ResearchActionNameEnum.PulledSourceError, { url, scraperType });
    this._track.storeActionEvent(ResearchActionNameEnum.PulledSourceError, { url, scraperType });
  };

  userLikeSource = (url: string) => {
    // @WARNING: This event has old name in amplitude and new name in strore.
    // so using old name for now.
    this._track.ampEvent('rshr/user/result/like');
    this._track.storeActionEvent('rshr/user/result/like', { url });
  };

  userDislikeSource = (url: string, reason?: { [key: string]: unknown }) => {
    this._track.ampEvent('rshr/user/result/dislike');
    this._track.storeActionEvent('rshr/user/result/dislike', { url, reason });
  };

  userClickedOpen = (triggeredBy: 'Research' | 'Bookmark', url: string) => {
    this._track.ampEvent(this._events.UserClickedOpenResult, { triggeredBy, url });
    this._track.storeActionEvent(this._events.UserClickedOpenResult, { triggeredBy, url });
  };

  userOpenedCardHighlights = (url: string) => {
    // these are non_state actions so better keep it this way
    this._track.ampEvent(this._events.UserOpenedCardHighlights);
    this._track.storeActionEvent(this._events.UserOpenedCardHighlights, { url });
  };

  userOpenedStatsAccordion = (url: string) => {
    this._track.ampEvent(this._events.UserOpenedStatsAccordion);
    this._track.storeActionEvent(this._events.UserOpenedStatsAccordion, { url });
  };

  userSelectedTopicInResultCard = (triggeredBy: 'Research' | 'Bookmark') => {
    this._track.ampEvent(this._events.UserSelectedTopics, { triggeredBy });
    this._track.storeActionEvent(this._events.UserSelectedTopics, { triggeredBy });
  };

  userSelectedMasterTopic = (chipName: string) => {
    this._track.ampEvent(ResearchActionNameEnum.SetSelectedMasterTopic);
    this._track.storeActionEvent(ResearchActionNameEnum.SetSelectedMasterTopic);
  };

  clickedSummarizeArtcile = (isNews: boolean) => {
    this._track.ampEvent(this._events.UserClickedResultCardSummarize, { isNews });
    this._track.storeActionEvent(this._events.UserClickedResultCardSummarize, { isNews });
  };

  userClickedBookmarkButton = (url: string, isBookMarked: boolean) => {
    this._track.ampEvent(BookmarkActionNameEnum.SaveBookmarkSuccess, { isBookMarked });
    this._track.storeActionEvent(BookmarkActionNameEnum.SaveBookmarkSuccess, {
      isBookMarked,
      url,
    });
  };

  clickedResultCardUrl = (url: string) => {
    this._track.ampEvent(this._events.ClickedResultCardURL);
    this._track.storeActionEvent(this._events.ClickedResultCardURL, { url });
  };

  tabSwitched = (tabName: string, isSwitchedByUser: boolean) => {
    let actionName = this._events.UserSwitchTab;
    if (!isSwitchedByUser) actionName = this._events.SystemSwitchTab;
    this._track.ampEvent(actionName, { tabName });
    // this._track.storeActionEvent(actionName, { tabName });
  };

  /**
   * Notebook ToolPane
   */

  userDeletedBookmark = (url: string, isBookMarked: boolean) => {
    this._track.ampEvent(BookmarkActionNameEnum.DeleteBookmarkSuccess, { url });
    this._track.storeActionEvent(BookmarkActionNameEnum.DeleteBookmarkSuccess, {
      isBookMarked,
      url,
    });
  };

  userNotebookSwitchSubTab = (active: string, tabInfo?: TabSwitchOptions) => {
    this._switchTab(active, {
      from: tabInfo?.from,
      parent: tabInfo?.parent || ManifoldTabEnum.NOTEBOOK,
      isSwitchedByUser: tabInfo?.isSwitchedByUser,
    });
  };

  userInteractedWithScratchpad = () => {
    if (this._countAddedScratchpadContent < 1) {
      this._countAddedScratchpadContent += 1;
      this._track.ampEvent(this._events.UserInteractedWithScratchpad);
    }
  };

  /**
   * Citation ToolPane
   */

  citationCardActions = (actionName: string, styleName?: string, citationData?: CitationData) => {
    let action = '';
    let data = {};
    if (actionName === 'delete') action = this._events.UserDeleteCitation;
    else if (actionName === 'edit') action = this._events.UserClickedEditCitation;
    else if (actionName === 'find_on_page') action = this._events.UserClickedFindOnPage;
    else if (actionName === 'find_source') action = this._events.UserClickedCitationSource;
    else if (actionName === 'add_citation') action = this._events.UserClickedCardAddCitationButton;
    else if (actionName === 'close_modal') action = this._events.UserClosedAddCitationModal;
    else if (actionName === 'add_citation_marker') {
      action = this._events.UserClickedAddCitationMarkerButton;
    } else if (actionName === 'style_change') {
      action = this._events.UserChangeCitationStyle;
      data = { styleName };
    } else if (actionName === 'citation_added_from_modal') {
      action = this._events.UserSubmittedCitationFromModal;
      data = { citationData };
    }
    this._track.ampEvent(action);
    this._track.storeActionEvent(action, data);
  };

  searchWithinCitation = (searchQuery: string, citationDataArray: CitationData[]) => {
    this._track.ampEvent(this._events.SearchWithinCitation, {
      searchResultCount: citationDataArray.length,
    });
    this._track.storeActionEvent(this._events.SearchWithinCitation, {
      searchResultCount: citationDataArray.length,
      searchQuery,
    });
  };

  /**
   * onboarding
   */

  closeOnboarding = (isSkipped: boolean, skippedFrom: string) => {
    if (isSkipped) this._track.ampEvent(this._events.SkipOnboarding, { triggeredBy: skippedFrom });
    else this._track.ampEvent(this._events.CloseOnboarding);
  };

  reachedLastSlide = () => {
    this._track.ampEvent(this._events.ReachedLastSlide);
  };

  onboardingActionPerformed = (didUserPerformedAction: boolean) => {
    if (didUserPerformedAction) {
      this._track.ampEvent(this._events.UserPerformedAction);
    } else {
      this._track.ampEvent(this._events.UserUsedNavigationButton);
    }
  };

  /**
   * EVENT FOR RIGHT PANE
   */

  clickedRephraseButton = () => {
    this._track.ampEvent(this._events.ClickedRephraseButton);
    this._track.storeActionEvent(this._events.ClickedRephraseButton);
  };

  clickedParaphraseSummaryButton = () => {
    this._track.ampEvent(this._events.ClickedParaphraseSummaryButton);
    this._track.storeActionEvent(this._events.ClickedParaphraseSummaryButton);
  };

  // triggeredAutocompleter = (preText: string, postText?: string) => {
  //   this._track.ampEvent(this._events.TriggeredAutocompletion, {
  //     preTextWordCount: preText.trim().split(/\s/).length,
  //   });
  //   this._track.storeActionEvent(this._events.TriggeredAutocompletion, { preText });
  // };

  autocompleterResposneShown = (suggestions: BaseEditorAutoCompleteSourceData[]) => {
    this._track.ampEvent(SYSTEM_SUGGESTION_SHOWN, {
      suggestionCount: suggestions.length,
    });
    this._track.storeActionEvent(SYSTEM_SUGGESTION_SHOWN, { suggestionCount: suggestions.length });
  };

  userSelectedSuggestion = ({
    selectedSuggestion,
    modelName,
    m1Suggestions,
    m2Suggestions,
    preText,
  }: {
    selectedSuggestion: string;
    modelName: AutoSuggestModelName;
    m1Suggestions: string[];
    m2Suggestions: string[];
    preText: string;
  }) => {
    this._track.ampEvent(USER_SELECTED_SUGGESTION, {
      modelName,
    });
    this._track.storeActionEvent(USER_SELECTED_SUGGESTION, {
      selectedSuggestion,
      modelName,
      m1Suggestions,
      m2Suggestions,
      preText,
    });
  };

  userClickedCitationMarker = () => {
    this._track.ampEvent(this._events.UserClickedCitationMarker);
    this._track.storeActionEvent(this._events.UserClickedCitationMarker);
    this._switchTab(ManifoldTabEnum.CITATIONS, {
      from: 'EDITOR',
      isSwitchedByUser: false,
    });
  };

  userReachedStorageQuota = (type: 'info' | 'warning' | 'critial') => {
    if (type === 'info' && this._countInfoStorage < 1) {
      this._countInfoStorage += 1;
      this._track.ampEvent(this._events.UserReachedStorageQuota, { type });
    } else if (type === 'warning' && this._countWarningStorage < 1) {
      this._countWarningStorage += 1;
      this._track.ampEvent(this._events.UserReachedStorageQuota, { type });
    } else if (type === 'critial' && this._countCriticalStorage < 1) {
      this._countCriticalStorage += 1;
      this._track.ampEvent(this._events.UserReachedStorageQuota, { type });
    }
  };

  systemTriggeredPremiumUpsellModal = () => {
    this._track.ampEvent(this._events.SystemTriggeredPremiumUpsellModal);
  };

  clickViewMoreHighlights = () => {
    this._track.ampEvent(this._events.UserClickedViewAllHighlights);
  };

  userInteractedWithHighlight = () => {
    this._track.ampEvent(this._events.UserInteractedWithHighlights);
  };

  writeAssistTooltipShown = () => {
    this._track.ampEvent(this._events.WriteAssistTooltipShown);
  };

  gcPaintToggle = (active: boolean) => {
    this._track.ampEvent(TOGGLE_PAINT_GRAMMAR_RESPONSE, { active });
  };

  clickedHelp = (
    triggeredBy: 'File Menu' | 'Sidebar' | 'Landing Page',
    type: 'FAQs' | 'Tutorial'
  ) => {
    this._track.ampEvent(this._events.UserClickedHelpButton, { triggeredBy, type });
  };

  switchView = (view: any) => {
    this._track.ampEvent(this._events.UserSwitchedView, { type: view });
  };

  sortedFor = (type: any) => {
    this._track.ampEvent(this._events.UserSortedProject, { type });
  };

  openedTrashView = () => {
    this._track.ampEvent(this._events.UserOpenedTrashView);
  };

  openedProjectNewTab = () => {
    this._track.ampEvent(this._events.UserOpenedProjectNewTab);
  };

  restoredProject = () => {
    this._track.ampEvent(this._events.UserRestoredProject);
  };

  exitedProject = (triggeredBy: 'Exit' | 'Open Project') => {
    this._track.ampEvent(this._events.UserExitedProject, triggeredBy);
  };

  openedFeedback = () => {
    this._track.ampEvent(this._events.UserOpenedFeedback);
  };

  interactedWithOnboarding = () => {
    this._track.ampEvent(this._events.UserInteractedWithOnboarding);
  };

  toggleBookmarkDrawer = (type: boolean) => {
    this._track.ampEvent(this._events.UserToggledBookmarkDrawer, { type });
  };

  openedLinkNewTab = (triggeredBy: 'Research' | 'Bookmark', url: string) => {
    this._track.ampEvent(this._events.UserOpenedLinkNewTab, { triggeredBy, url });
  };

  openedAccordion = (triggeredBy: 'Research' | 'Bookmark', type: string) => {
    this._track.ampEvent(this._events.UserOpenedSection, { triggeredBy, type });
  };

  paragraphMoved = (triggeredBy: 'Paraphraser' | 'Summarizer', type: 'Previous' | 'Next') => {
    this._track.ampEvent(this._events.UserMovedParagraph, { type });
  };

  summmarizerModeChanged = (summaryMode: 'abs' | 'ext') => {
    this._track.ampEvent(this._events.UserChangedSummaryMode, { summaryMode });
  };

  summarizerSummaryLengthChanged = (summaryLength: number) => {
    this._track.ampEvent(this._events.UserChangedSummaryLength, { summaryLength });
  };

  popperOpened = (type: 'Statistics' | 'Grammar Settings' | 'Help') => {
    this._track.ampEvent(this._events.UserOpenedPopper, { type });
  };

  resultCardClicked = () => {
    this._track.ampEvent(this._events.UserClickedResultCard);
  };
}

const researcherTrack = new ResearcherTrack();
export default researcherTrack;
